import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type Ref } from 'react'
import { Icon } from '../ui'
import './picker.css'

// Choosing one thing from a list too long to read down.
//
// # WHY THIS EXISTS
//
// A native select draws itself as wide as its longest option and offers
// nothing to type into. "Ask about an answer" listed 75 questions in a control
// 1,302 px wide inside a 540 px drawer, and a tenant with three hundred
// vendors scrolled all of them to record one finding (the founder's review of
// 11 October 2026). This is the one control for those lists: type part of a
// name or a code, move with the arrow keys, Enter chooses, Escape gives up.
//
// # WHERE THE LINE IS
//
// A list goes here when using the product grows it without a ceiling (one row
// per vendor, finding, question, document, import, person), or when it is a
// catalogue already past twelve. A handful of statuses, tiers or bands stays a
// native select: it is read at a glance, and a phone gives it its own wheel.
//
// # TWO FACES
//
// At rest with something chosen it is a button that says the choice in full,
// wrapped over as many lines as it needs. Pressing it, or typing on it, turns
// it into the search box with the list under it. With nothing chosen the
// search box is what rests, so a form that puts the keyboard on its first
// field lands somewhere that can be typed into straight away. Where an empty
// value is itself a choice ("No document"), the box says so where its
// placeholder goes and still holds no letters of its own.
//
// # ONE BEHAVIOUR
//
// What the box holds (pickerStep), what a key means to it (pickerKey), what
// it shows (pickerBox) and what its list holds (pickerListing) are plain
// functions with no component in them. The first and third were written for
// the vendor picker and put right by two reviews of the Evidence screen; they
// are here unchanged, so every list is chosen from by those rules.
//
// # WHAT IT DOES NOT DO
//
// A field of a form does not open because the keyboard arrived. A dialog
// that focuses its first field would open a list over the form before
// anybody asked, and so would every Tab through a page. The one box that
// does (opensOnFocus) is a scope already in force, which opens to say so.
//
// # WHY THE LIST IS IN THE TOP LAYER
//
// A drawer's body scrolls, a pop-up is short, and a filter panel clips. A list
// positioned under its box inside any of them is cut off or pushes a scroll
// bar into the dialog. The list stays where it is in the document, so it is
// still inside the dialog for a screen reader and for the dialog's own focus
// rules, and is drawn in the browser's top layer, placed against the box by
// hand. A browser with no top layer draws it under the box, as before.

export type PickerOption = {
  // What the caller is handed back. Never drawn.
  value: string
  label: string
  // A short code drawn beside the label and found by typing it: a vendor
  // code, a question key.
  code?: string
  // Other words the option is found by and that are not drawn: the name on a
  // vendor's papers where it trades under another.
  also?: string[]
}

// Everything that answers to what has been typed. Every word typed has to be
// somewhere in the option, in any order, so "labs meridian" and "meridian
// labs" find the same vendor. With nothing typed the whole list answers,
// unless the caller would rather it waited for a letter.
//
// The choice that is no choice is offered only while nothing is typed. It
// used to be offered whenever the letters typed were somewhere in its own
// words, first in the list and so the one Enter picked: "ant", typed for a
// vendor, is in "tenant", and Enter chose the whole tenant (first review of
// the Evidence screen, 11 October 2026). Somebody who has typed is looking
// for one of the others.
export function pickerMatches(options: PickerOption[], typed: string, none?: string, browse = true): PickerOption[] {
  const words = typed.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    const first: PickerOption[] = none ? [{ value: '', label: none }] : []
    return browse ? first.concat(options) : first
  }
  return options.filter((o) =>
    words.every((w) => [o.label, o.code, ...(o.also ?? [])].some((t) => (t ?? '').toLowerCase().includes(w))),
  )
}

// The line under a list that is not the whole of what matched. "private" finds
// two hundred vendors in a register of three hundred, and eight rows with
// nothing under them would read as eight vendors.
export function heldBackNote(listed: number, matched: number, typed = true): string {
  if (matched <= listed) return ''
  return (
    'The first ' + listed + ' of ' + matched + ' are listed. ' + (typed ? 'Type more to narrow them.' : 'Type to find the others.')
  )
}

// What the open list holds for what has been typed: the rows it draws, and
// whichever line it has to say under them.
export type Listing = { shown: PickerOption[]; idle: string; heldBack: string; nothing: string }

export function pickerListing(
  options: PickerOption[] | null,
  typed: string,
  o: { none?: string; browse?: boolean; hint?: string; limit?: number; noun?: string } = {},
): Listing {
  const matched = pickerMatches(options ?? NOTHING, typed, o.none, o.browse ?? true)
  const shown = matched.slice(0, o.limit ?? LIMIT)
  const some = typed.trim() !== ''
  return {
    shown,
    idle: !some && o.hint ? o.hint : '',
    heldBack: heldBackNote(shown.length, matched.length, some),
    // A list still being read has not failed to match anything yet.
    nothing: some && options !== null && shown.length === 0 ? 'No ' + (o.noun ?? 'choice') + ' matches' : '',
  }
}

// What the box shows. Its value is only ever what was typed, so the next
// letter typed is the start of a search and never lands on the end of a
// label. What an empty value means is said in the placeholder's place while
// the list is closed, and how to search while it is open.
export function pickerBox(typed: string, open: boolean, none: string | undefined, placeholder: string): { value: string; placeholder: string } {
  return { value: typed, placeholder: none && !open ? none : placeholder }
}

// What the box holds, whether its list is open and which row is lit, and what
// each thing a person does to it makes of that. Plain code, so the sequences
// two reviews found wrong (11 October 2026) can be played in a test: a letter
// typed after a pick or after Escape begins a new search, and a click or the
// Down key on a box whose list was closed opens it again.
export type PickerState = { text: string; open: boolean; active: number }

export type PickerEvent =
  | { type: 'focus' }
  | { type: 'click' }
  | { type: 'typed'; text: string }
  // rows is how many the list holds, which is as far down as Down goes.
  | { type: 'down'; rows: number }
  | { type: 'up' }
  | { type: 'escape' }
  | { type: 'picked' }
  // The focus, or a press of the mouse, went somewhere else.
  | { type: 'left' }

export const PICKER_AT_REST: PickerState = { text: '', open: false, active: 0 }

export function pickerStep(s: PickerState, e: PickerEvent): PickerState {
  switch (e.type) {
    case 'focus':
    case 'click':
      return s.open ? s : { ...s, open: true }
    case 'typed':
      return { text: e.text, open: true, active: 0 }
    case 'down':
      // On a closed box the first press opens the list where it was.
      return s.open ? { ...s, active: Math.max(0, Math.min(s.active + 1, e.rows - 1)) } : { ...s, open: true }
    case 'up':
      return { ...s, active: Math.max(s.active - 1, 0) }
    case 'escape':
    case 'picked':
      return PICKER_AT_REST
    case 'left':
      // The letters stay. They are not a choice, and a screen that acts on
      // the choice has to be able to see that there are some (onTyped).
      return s.open ? { ...s, open: false } : s
  }
}

export type PickerMove = {
  // What the key does to the box, when it does anything.
  event?: PickerEvent
  // The option the key chose, when it chose one.
  picked?: PickerOption
  // The key was used here, so nothing behind the picker sees it: Escape that
  // closed a list does not also close the drawer the list is in, and Enter
  // that chose a row does not also submit the form.
  spent: boolean
}

// What one key means to the box as it stands. Kept apart from the component
// so the rule is one function, and so it can be held to without a browser.
export function pickerKey(s: PickerState, key: string, listing: Listing): PickerMove {
  const listed = s.open && listing.shown.length > 0
  // Something of the list is on the screen: its rows, or that it has none.
  const showing = listed || (s.open && listing.nothing !== '')
  switch (key) {
    case 'ArrowDown':
      return { event: { type: 'down', rows: listing.shown.length }, spent: true }
    case 'ArrowUp':
      return { event: { type: 'up' }, spent: true }
    case 'Enter': {
      // Enter picks the lit row of a list that is on the screen, and nothing
      // from one that is not: on a closed box the key is the form's.
      if (!showing) return { spent: false }
      const picked = listed ? listing.shown[s.active] : undefined
      // Enter on "nothing matches" chooses nothing and submits nothing.
      return picked ? { event: { type: 'picked' }, picked, spent: true } : { spent: true }
    }
    case 'Escape':
      // A search box clears itself on Escape, and that clearing is an edit,
      // which opened the list again the moment it closed. The clearing is
      // done by the step instead, once.
      return showing || s.text !== '' ? { event: { type: 'escape' }, spent: true } : { spent: false }
    default:
      return { spent: false }
  }
}

// The open list: the listbox half of the combobox pattern. The box names the
// row the arrow keys are on by its id (aria-activedescendant), so the focus
// never leaves the box and the row is still what a screen reader says. Apart
// from the Picker so that markup can be held to on its own.
export function PickerList({
  id,
  label,
  listing,
  at,
  onPick,
  onPoint,
  popRef,
}: {
  id: string
  label: string
  listing: Listing
  at: number
  onPick: (o: PickerOption) => void
  onPoint: (i: number) => void
  popRef?: Ref<HTMLDivElement>
}): JSX.Element {
  return (
    // A press on the list's edge or its scroll bar is not a press somewhere
    // else: without this the box loses the focus and the list, which closes
    // when the focus leaves, closes under the pointer.
    <div ref={popRef} className="vpick-pop" popover="manual" onMouseDown={(e) => e.preventDefault()}>
      {listing.shown.length > 0 && (
        // Not a ul of li: the picker is drawn inside lists that style their
        // own (the approval chain numbers every li it holds), and a row of
        // choices is not an item of theirs.
        <div className="vpick-list" id={id + '-list'} role="listbox" aria-label={label}>
          {listing.shown.map((o, i) => (
            <div
              key={o.value || 'none'}
              id={id + '-opt-' + i}
              role="option"
              aria-selected={i === at}
              className={'vpick-opt' + (i === at ? ' on' : '')}
              onMouseDown={(e) => {
                e.preventDefault()
                onPick(o)
              }}
              onMouseMove={() => {
                if (i !== at) onPoint(i)
              }}
            >
              <span>{o.label}</span>
              {o.code && <span className="mono muted">{o.code}</span>}
            </div>
          ))}
        </div>
      )}
      {listing.idle && <div className="vpick-hint">{listing.idle}</div>}
      {listing.heldBack && <div className="vpick-hint">{listing.heldBack}</div>}
      {listing.nothing && <div className="vpick-hint">{listing.nothing}</div>}
    </div>
  )
}

const NOTHING: PickerOption[] = []
const LIMIT = 100
// The list is never taller than this, and never so short that it is a slit.
const TALLEST = 320
const SHORTEST = 120
const GAP = 4
const EDGE = 8

export type PickerProps = {
  id: string
  // The field's name. With `bare` the caller draws the visible label itself,
  // as <label htmlFor={id}>, and this is what the list and the chosen value
  // are named by for a screen reader.
  label: string
  value: string
  onChange: (value: string) => void
  // Null while the list is still being read.
  options: PickerOption[] | null
  placeholder?: string
  // What an empty value means, in words, where it means something: "Every
  // vendor in this tenant", "No document". It is what the box says while
  // nothing is chosen and the first thing the list offers. Absent, an empty
  // value is nothing chosen yet.
  none?: string
  // False where the list should wait for a letter before it offers anything
  // but the `none` choice.
  browse?: boolean
  // Said under the list while nothing is typed.
  hint?: string
  // False where the field always holds a value and has no way back to none.
  clearable?: boolean
  disabled?: boolean
  invalid?: boolean
  describedBy?: string
  // The browser's own "fill this in" on a form that leaves validation to it.
  required?: boolean
  // How many rows the list draws before it says how many it left out.
  limit?: number
  // What one row is, for "No vendor matches".
  noun?: string
  // In a row of controls: a pill, and the label read but not drawn.
  compact?: boolean
  // Only the control. The caller keeps its own label, hint and error line
  // around it, so a form changes one element and not its layout.
  bare?: boolean
  // The pixels it asks for in a row where each control takes what it needs.
  // Where the form gives it a width, that is its width and this is not used.
  width?: number
  // What to say for a value the list does not hold, where the value itself is
  // an identifier that tells a reader nothing. Absent, the value is shown: a
  // question key is worth reading, a record's id is not.
  missing?: string
  // The list opens when the keyboard arrives, for a box that is a scope
  // already in force and has that to say (the Evidence chain's). Not for a
  // field of a form: see WHAT IT DOES NOT DO.
  opensOnFocus?: boolean
  // Told whenever the letters in the box change, and with nothing when one is
  // chosen or they are cleared. Letters are not a choice: the value is still
  // what it was, and a screen about to act on the value can hold off while
  // the box shows a name nobody picked.
  onTyped?: (text: string) => void
}

export function Picker({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Type to search',
  none,
  browse = true,
  hint,
  clearable = true,
  disabled = false,
  invalid = false,
  describedBy,
  required = false,
  limit = LIMIT,
  noun = 'choice',
  compact = false,
  bare = false,
  width,
  missing,
  opensOnFocus = false,
  onTyped,
}: PickerProps): JSX.Element {
  const [state, setState] = useState<PickerState>(PICKER_AT_REST)
  const { text, open, active } = state
  // The search box is showing although something is chosen.
  const [searching, setSearching] = useState(false)
  // Choosing, clearing and giving up each take the focused element off the
  // page (the box becomes the button, or the button the box). This is raised
  // each time, and whichever face is then drawn takes the focus.
  const [focusAsk, setFocusAsk] = useState(0)
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const rest = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  // Whether this opening of the list is above the box. Decided once, when it
  // opens: a list that opened above for want of room and then jumped below
  // as typing shortened it would move under the reader's eye.
  const above = useRef<boolean | null>(null)

  const list = options ?? NOTHING
  // A value the list does not hold is still said, and not drawn as an empty
  // box: a condition naming a question that has since been removed has to be
  // seen to be put right.
  const held: PickerOption | null =
    value === ''
      ? null
      : (list.find((o) => o.value === value) ?? { value, label: options === null ? 'Reading the list' : (missing ?? value) })
  const resting = !searching && held !== null

  const listing = useMemo(
    () => pickerListing(options, text, { none, browse, hint, limit, noun }),
    [options, text, none, browse, hint, limit, noun],
  )
  const { shown, heldBack, nothing, idle } = listing
  const at = Math.min(active, Math.max(shown.length - 1, 0))
  const listed = open && !resting && shown.length > 0
  const popped = open && !resting && (shown.length > 0 || nothing !== '')
  const box = pickerBox(text, open, none, placeholder)

  function move(next: PickerState) {
    if (next.text !== text) onTyped?.(next.text)
    setState(next)
  }
  const send = (e: PickerEvent) => move(pickerStep(state, e))

  // The focus, or a press of the mouse, went somewhere else. Letters typed
  // into an empty box stay, as the step says. Letters typed over a choice do
  // not: the box would show a name nobody picked while the form still held
  // the one that was, so it goes back to saying what it holds.
  function left() {
    if (searching) {
      move(PICKER_AT_REST)
      setSearching(false)
    } else {
      send({ type: 'left' })
    }
  }
  const leftNow = useRef(left)
  leftNow.current = left

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) leftNow.current()
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  useLayoutEffect(() => {
    if (focusAsk > 0) (rest.current ?? input.current)?.focus()
  }, [focusAsk])

  // A form that leaves its check to the browser is refused while its box is
  // empty (required). Letters in the box would pass that check with nothing
  // chosen, so they are refused in words.
  useLayoutEffect(() => {
    input.current?.setCustomValidity(required && text.trim() !== '' ? 'Choose one from the list, or clear the box.' : '')
  }, [required, text, resting])

  // Into the top layer, and against the box. Placed again on every scroll and
  // resize, because the box moves when the drawer it is in is scrolled and
  // the room under it shrinks when a phone's keyboard comes up.
  useLayoutEffect(() => {
    const el = pop.current
    const anchor = input.current
    if (!popped) above.current = null
    if (!popped || !el || !anchor) return
    let top = false
    if (typeof el.showPopover === 'function') {
      try {
        if (!el.matches(':popover-open')) el.showPopover()
        top = true
      } catch {
        // Not shown: without the attribute it is an ordinary element again,
        // and the stylesheet draws it under the box.
        el.removeAttribute('popover')
      }
    }
    if (!top) return
    const place = () => {
      const r = anchor.getBoundingClientRect()
      const view = window.visualViewport
      const viewTop = view ? view.offsetTop : 0
      const viewHeight = view ? view.height : window.innerHeight
      const below = viewTop + viewHeight - r.bottom - GAP - EDGE
      const over = r.top - viewTop - GAP - EDGE
      const width = Math.max(r.width, Math.min(240, window.innerWidth - 2 * EDGE))
      el.style.width = width + 'px'
      el.style.left = Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE)) + 'px'
      el.style.maxHeight = TALLEST + 'px'
      // Above the box only when it does not fit under it and there is more
      // room above: at the foot of a drawer, or over a phone's keyboard.
      if (above.current === null) above.current = el.offsetHeight > below && over > below
      const up = above.current
      el.style.maxHeight = Math.max(SHORTEST, Math.min(TALLEST, up ? over : below)) + 'px'
      el.style.top = (up ? r.top - GAP - el.offsetHeight : r.bottom + GAP) + 'px'
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    window.visualViewport?.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      window.visualViewport?.removeEventListener('resize', place)
    }
  }, [popped, shown.length, heldBack, nothing, idle])

  useEffect(() => {
    if (listed) document.getElementById(id + '-opt-' + at)?.scrollIntoView({ block: 'nearest' })
  }, [listed, at, id])

  // From the button that says the choice to the search box. The list opens
  // on the row that is chosen, so the arrow keys start from where the reader
  // is and not from the top; a letter typed on the button starts the search
  // with it.
  function begin(first = '') {
    if (disabled) return
    const from = first === '' ? pickerListing(options, '', { none, browse, limit }).shown.findIndex((o) => o.value === value) : 0
    move({ text: first, open: true, active: Math.max(from, 0) })
    setSearching(true)
    setFocusAsk((n) => n + 1)
  }

  function pick(o: PickerOption) {
    onChange(o.value)
    send({ type: 'picked' })
    setSearching(false)
    setFocusAsk((n) => n + 1)
  }

  const canClear = held !== null && clearable && !disabled
  const caret = (
    <span className="vpick-caret" aria-hidden="true">
      <Icon name="chevron" size={14} />
    </span>
  )

  return (
    <div
      className={
        (bare ? '' : 'field ') + 'vpick' + (compact ? ' vpick-compact' : '') + (none ? ' vpick-default' : '') + (open ? ' vpick-open' : '')
      }
      style={width ? ({ '--vpick-width': width + 'px' } as CSSProperties) : undefined}
      ref={wrap}
      onBlur={(e) => {
        // The list does not stay open behind a reader who has tabbed on.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) left()
      }}
    >
      {!bare && (
        <label htmlFor={id} className={compact ? 'vh' : undefined}>
          {label}
        </label>
      )}
      <span className="vpick-face">
        {resting ? (
          <span className={'vpick-chosen' + (disabled ? ' vpick-locked' : '')} data-invalid={invalid || undefined}>
            <button
              ref={rest}
              id={id}
              type="button"
              className="vpick-value"
              aria-haspopup="listbox"
              aria-expanded={false}
              aria-label={label + ': ' + held.label + (held.code ? ' ' + held.code : '')}
              aria-describedby={describedBy}
              disabled={disabled}
              onClick={() => begin()}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  begin()
                } else if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                  // A letter typed on a select finds an option, so a letter
                  // typed here starts the search with it.
                  e.preventDefault()
                  begin(e.key)
                }
              }}
            >
              <span>{held.label}</span>
              {held.code && <span className="mono muted">{held.code}</span>}
              {!canClear && !disabled && caret}
            </button>
            {canClear && (
              <button
                type="button"
                className="vpick-clear"
                aria-label={'Clear ' + held.label}
                onClick={() => {
                  onChange('')
                  send({ type: 'picked' })
                  setSearching(false)
                  setFocusAsk((n) => n + 1)
                }}
              >
                ×
              </button>
            )}
          </span>
        ) : (
          <>
            <input
              id={id}
              ref={input}
              type="search"
              role="combobox"
              aria-expanded={listed}
              aria-controls={id + '-list'}
              aria-activedescendant={listed ? id + '-opt-' + at : undefined}
              aria-autocomplete="list"
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              autoComplete="off"
              value={box.value}
              placeholder={box.placeholder}
              disabled={disabled}
              required={required}
              onChange={(e) => send({ type: 'typed', text: e.target.value })}
              onFocus={() => {
                if (opensOnFocus) send({ type: 'focus' })
              }}
              // A box that already has the focus gets no focus event from a
              // click, so a list closed with Escape stayed closed under it.
              onClick={() => send({ type: 'click' })}
              onKeyDown={(e) => {
                const key = pickerKey({ text, open, active: at }, e.key, listing)
                if (!key.spent) return
                e.preventDefault()
                e.stopPropagation()
                if (key.picked) return pick(key.picked)
                if (key.event) send(key.event)
                if (e.key === 'Escape' && searching) {
                  setSearching(false)
                  setFocusAsk((n) => n + 1)
                }
              }}
            />
            {caret}
            {popped && <PickerList id={id} label={label} listing={listing} at={at} onPick={pick} onPoint={(i) => setState((s) => ({ ...s, active: i }))} popRef={pop} />}
          </>
        )}
      </span>
      {/* Always in the document, so what it comes to say is announced. */}
      <span className="vh" role="status">
        {popped ? nothing || heldBack : ''}
      </span>
    </div>
  )
}
